import React, { useEffect, useRef } from "react";
import { createChart, CrosshairMode, LineStyle } from "lightweight-charts";
import { fetchKlines, subscribeKline } from "@/lib/binance";
import { computeRSI, computeMACD, computeSMA, computeEMA, computeBollingerBands } from "@/lib/indicators";

export default function Chart({ asset, interval = "1m", indicator = "none", chartType = "area", openTrades = [], currentPrice }) {
  const symbol = asset?.symbol;
  const mainDivRef = useRef(null);
  const indDivRef = useRef(null);
  const chartsRef = useRef([]);
  const subRef = useRef(null);
  const candlesRef = useRef([]);
  const seriesRef = useRef(null);
  const priceLinesRef = useRef([]);
  // For IQ-style realtime tick line (area mode)
  const tickSeriesRef = useRef(null);
  const tickDataRef = useRef([]);
  const lastTickTimeRef = useRef(0);
  const chartInstanceRef = useRef(null);

  // (Re)build chart on symbol/interval/indicator/chartType change
  useEffect(() => {
    if (!mainDivRef.current) return;

    // IQ Option Premium chart layout
    const commonLayout = {
      layout: {
        background: { color: "transparent" },
        textColor: "#4a5568",
        fontFamily: "JetBrains Mono, monospace",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "rgba(31, 37, 53, 0.6)" },
        horzLines: { color: "rgba(31, 37, 53, 0.6)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: "rgba(0, 230, 118, 0.25)",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#1f2535",
        },
        horzLine: {
          color: "rgba(255, 152, 0, 0.5)",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#ff9800",
        },
      },
      rightPriceScale: {
        borderColor: "#1f2535",
        textColor: "#4a5568",
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      timeScale: {
        borderColor: "#1f2535",
        textColor: "#4a5568",
        timeVisible: true,
        secondsVisible: true,
        fixLeftEdge: false,
        fixRightEdge: false,
        rightOffset: 6,
      },
      localization: { locale: "en-US" },
    };

    const mainChart = createChart(mainDivRef.current, {
      ...commonLayout,
      width: mainDivRef.current.clientWidth || 400,
      height: mainDivRef.current.clientHeight || 300,
    });
    chartInstanceRef.current = mainChart;

    let mainSeries;
    if (chartType === "area") {
      // IQ Option Premium style: neon green line + rich gradient fill
      mainSeries = mainChart.addAreaSeries({
        lineColor: "#00e676",
        topColor: "rgba(0, 230, 118, 0.35)",
        bottomColor: "rgba(0, 230, 118, 0.01)",
        lineWidth: 2,
        priceLineColor: "#ff9800",
        priceLineWidth: 1,
        priceLineStyle: LineStyle.Dotted,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 6,
        crosshairMarkerBorderColor: "#00e676",
        crosshairMarkerBackgroundColor: "#00e676",
        lastValueVisible: true,
      });
    } else {
      mainSeries = mainChart.addCandlestickSeries({
        upColor: "#00e676", downColor: "#ff1744",
        borderUpColor: "#00e676", borderDownColor: "#ff1744",
        wickUpColor: "#00e676", wickDownColor: "#ff1744",
      });
    }
    seriesRef.current = mainSeries;

    // Reset tick data for realtime line
    tickSeriesRef.current = null;
    tickDataRef.current = [];
    lastTickTimeRef.current = 0;

    let indChart = null;
    const indSeries = {};
    const mainSeriesExtra = {};
    
    // Handle indicators that are plotted on main chart vs indicator chart
    const isMainChartIndicator = ["sma", "ema", "bb"].includes(indicator);
    
    if (!isMainChartIndicator && indicator !== "none" && indDivRef.current) {
      indChart = createChart(indDivRef.current, { 
        ...commonLayout, 
        width: indDivRef.current.clientWidth || 400, 
        height: indDivRef.current.clientHeight || 150 
      });
      if (indicator === "rsi") {
        indSeries.rsi = indChart.addLineSeries({ color: "#FBBF24", lineWidth: 2 });
        indSeries.ref30 = indChart.addLineSeries({ color: "#71717A", lineWidth: 1, lineStyle: LineStyle.Dashed });
        indSeries.ref70 = indChart.addLineSeries({ color: "#71717A", lineWidth: 1, lineStyle: LineStyle.Dashed });
      } else if (indicator === "macd") {
        indSeries.hist = indChart.addHistogramSeries({ base: 0 });
        indSeries.macd = indChart.addLineSeries({ color: "#3B82F6", lineWidth: 2 });
        indSeries.signal = indChart.addLineSeries({ color: "#F59E0B", lineWidth: 2 });
      }
      mainChart.timeScale().subscribeVisibleLogicalRangeChange((r) => { if (r && indChart) indChart.timeScale().setVisibleLogicalRange(r); });
      indChart.timeScale().subscribeVisibleLogicalRangeChange((r) => { if (r) mainChart.timeScale().setVisibleLogicalRange(r); });
    }

    const ro = new ResizeObserver(() => {
      try {
        if (mainDivRef.current && mainDivRef.current.clientWidth > 0 && mainDivRef.current.clientHeight > 0) {
          mainChart.resize(mainDivRef.current.clientWidth, mainDivRef.current.clientHeight);
        }
        if (indChart && indDivRef.current && indDivRef.current.clientWidth > 0 && indDivRef.current.clientHeight > 0) {
          indChart.resize(indDivRef.current.clientWidth, indDivRef.current.clientHeight);
        }
      } catch (e) {
        console.error("Resize error", e);
      }
    });
    
    if (mainDivRef.current) ro.observe(mainDivRef.current);
    if (indDivRef.current && !isMainChartIndicator && indicator !== "none") ro.observe(indDivRef.current);

    const applyIndicator = (candles) => {
      // First, remove any existing main chart extra series
      Object.values(mainSeriesExtra).forEach((s) => { try { mainChart.removeSeries(s); } catch {} });
      
      if (indicator === "none") return;
      
      if (indicator === "rsi" && indChart) {
        const rsi = computeRSI(candles, 14);
        indSeries.rsi.setData(rsi);
        if (rsi.length) {
          const t0 = rsi[0].time, t1 = rsi[rsi.length - 1].time;
          indSeries.ref30.setData([{ time: t0, value: 30 }, { time: t1, value: 30 }]);
          indSeries.ref70.setData([{ time: t0, value: 70 }, { time: t1, value: 70 }]);
        }
      } else if (indicator === "macd" && indChart) {
        const m = computeMACD(candles);
        indSeries.hist.setData(m.hist);
        indSeries.macd.setData(m.macd);
        indSeries.signal.setData(m.signal);
      } else if (indicator === "sma") {
        const smaData = computeSMA(candles, 20);
        mainSeriesExtra.sma = mainChart.addLineSeries({ color: "#3B82F6", lineWidth: 2 });
        mainSeriesExtra.sma.setData(smaData);
      } else if (indicator === "ema") {
        const emaData = computeEMA(candles, 20);
        mainSeriesExtra.ema = mainChart.addLineSeries({ color: "#EC4899", lineWidth: 2 });
        mainSeriesExtra.ema.setData(emaData);
      } else if (indicator === "bb") {
        const bb = computeBollingerBands(candles, 20, 2);
        mainSeriesExtra.bbMiddle = mainChart.addLineSeries({ color: "#F59E0B", lineWidth: 1 });
        mainSeriesExtra.bbUpper = mainChart.addLineSeries({ color: "#10B981", lineWidth: 1, lineStyle: LineStyle.Dashed });
        mainSeriesExtra.bbLower = mainChart.addLineSeries({ color: "#EF4444", lineWidth: 1, lineStyle: LineStyle.Dashed });
        mainSeriesExtra.bbMiddle.setData(bb.middle);
        mainSeriesExtra.bbUpper.setData(bb.upper);
        mainSeriesExtra.bbLower.setData(bb.lower);
      }
    };

    let mounted = true;
    (async () => {
      try {
        const data = await fetchKlines(asset, interval, 300);
        if (!mounted || !data?.length) return;
        candlesRef.current = data;
        if (chartType === "area") {
          mainSeries.setData(data.map((k) => ({ time: k.time, value: k.close })));
        } else {
          mainSeries.setData(data);
        }
        applyIndicator(data);
        mainChart.timeScale().fitContent();
      } catch (e) { console.error("chart load error:", e); }
    })();

    const sub = subscribeKline(asset, interval, (c) => {
      try {
        if (chartType === "area") {
          mainSeries.update({ time: c.time, value: c.close });
        } else {
          mainSeries.update(c);
        }
        const arr = candlesRef.current;
        if (arr.length && arr[arr.length - 1].time === c.time) arr[arr.length - 1] = c;
        else arr.push(c);
        applyIndicator(arr);
      } catch {}
    });
    subRef.current = sub;

    return () => {
      mounted = false;
      try { ro.disconnect(); } catch {}
      try { sub && sub.close && sub.close(); } catch {}
      try { mainChart.remove(); } catch {}
      try { indChart && indChart.remove(); } catch {}
      priceLinesRef.current = [];
      tickSeriesRef.current = null;
      tickDataRef.current = [];
      lastTickTimeRef.current = 0;
      chartInstanceRef.current = null;
    };
  }, [symbol, interval, indicator, chartType, asset]);

  // ═══ IQ Option style: realtime tick-by-tick line that moves every second ═══
  // Instead of only updating the last candle's close, we add a new data point
  // every second so the chart line visually "moves forward" in real time.
  useEffect(() => {
    if (!seriesRef.current || currentPrice == null) return;
    const arr = candlesRef.current;
    if (!arr.length) return;

    const now = Math.floor(Date.now() / 1000);
    const last = arr[arr.length - 1];
    if (!last) return;

    // Update the candle data
    const updated = {
      ...last,
      close: currentPrice,
      high: Math.max(last.high, currentPrice),
      low: Math.min(last.low, currentPrice),
    };
    arr[arr.length - 1] = updated;

    try {
      if (chartType === "area") {
        // For area chart: add a data point at the current second timestamp
        // This makes the line visibly advance every tick (IQ Option style)
        const pointTime = Math.max(now, last.time);
        
        // Avoid duplicate times — only update if time is >= last tick time
        if (pointTime > lastTickTimeRef.current) {
          seriesRef.current.update({ time: pointTime, value: currentPrice });
          lastTickTimeRef.current = pointTime;
        } else {
          // Same second, just update the value
          seriesRef.current.update({ time: lastTickTimeRef.current, value: currentPrice });
        }

        // Auto-scroll to keep the latest point visible
        if (chartInstanceRef.current) {
          chartInstanceRef.current.timeScale().scrollToRealTime();
        }
      } else {
        // Candle chart: just update the current candle
        seriesRef.current.update(updated);
      }
    } catch {}
  }, [currentPrice, chartType]);

  // Redraw trade price lines & markers on openTrades change
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    // Remove existing lines
    priceLinesRef.current.forEach((pl) => { try { series.removePriceLine(pl); } catch {} });
    priceLinesRef.current = [];

    const relevant = (openTrades || []).filter((t) => t.symbol === symbol);
    relevant.forEach((t) => {
      const isUp = t.direction === "call" || t.direction === "buy";
      const color = isUp ? "#00c853" : "#ef4444";
      try {
        const pl = series.createPriceLine({
          price: t.entry_price,
          color,
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: `$${Number(t.amount).toFixed(0)}`,
        });
        priceLinesRef.current.push(pl);
      } catch {}
      // Also TP / SL price lines for CFDs
      if (t.take_profit) {
        try {
          const pl = series.createPriceLine({
            price: t.take_profit, color: "#22C55E", lineWidth: 1,
            lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: "TP",
          });
          priceLinesRef.current.push(pl);
        } catch {}
      }
      if (t.stop_loss) {
        try {
          const pl = series.createPriceLine({
            price: t.stop_loss, color: "#EF4444", lineWidth: 1,
            lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: "SL",
          });
          priceLinesRef.current.push(pl);
        } catch {}
      }
    });

    // Marker at entry point for each trade
    const markers = relevant.map((t) => ({
      time: Math.floor(new Date(t.opened_at).getTime() / 1000),
      position: (t.direction === "call" || t.direction === "buy") ? "belowBar" : "aboveBar",
      color: (t.direction === "call" || t.direction === "buy") ? "#00c853" : "#ef4444",
      shape: (t.direction === "call" || t.direction === "buy") ? "arrowUp" : "arrowDown",
      text: `$${Number(t.amount).toFixed(0)}`,
    }));
    try { series.setMarkers(markers); } catch {}
  }, [openTrades, symbol]);

  return (
    <div className="absolute inset-0 world-map-bg" style={{ background: "#0d0f17" }}>
      {/* Subtle ambient radial glow */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse 60% 40% at 50% 100%, rgba(0,230,118,0.04), transparent)",
        zIndex: 1,
      }} />
      <div 
        ref={mainDivRef} 
        style={{ 
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%", 
          height: (indicator === "none" || ["sma", "ema", "bb"].includes(indicator)) ? "100%" : "70%",
          zIndex: 2,
        }} 
      />
      <div 
        ref={indDivRef} 
        style={{ 
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%", 
          height: "30%", 
          display: (indicator === "none" || ["sma", "ema", "bb"].includes(indicator)) ? "none" : "block",
          borderTop: "1px solid rgba(31,37,53,0.8)",
          zIndex: 2,
        }} 
      />
    </div>
  );
}
